import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Search, Filter, BookOpen, Eye, Heart, Clock, User, Trophy } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

const StoriesPage = () => {
  const { user, api } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stories, setStories] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedGenre, setSelectedGenre] = useState(searchParams.get('genre') || '');

  const handleLike = async (e, storyId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    try {
      const res = await api.post(`/stories/${storyId}/like`);
      setStories(prev => prev.map(s => 
        s.id === storyId ? { ...s, like_count: s.like_count + (res.data.liked ? 1 : -1) } : s
      ));
    } catch (err) { console.error('Like failed:', err); }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [storiesRes, genresRes] = await Promise.all([
          api.get('/stories', {
            params: {
              genre: selectedGenre || undefined,
              search: searchQuery || undefined
            }
          }),
          api.get('/genres')
        ]);
        setStories(storiesRes.data);
        setGenres(genresRes.data);
      } catch (error) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, selectedGenre, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams({ search: searchQuery, genre: selectedGenre });
  };

  const handleGenreChange = (value) => {
    setSelectedGenre(value === 'all' ? '' : value);
    setSearchParams({ search: searchQuery, genre: value === 'all' ? '' : value });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="stories-page">
      {/* Header */}
      <div className="text-center mb-12">
        <span className="overline text-brand-accent mb-4 block">Discover</span>
        <h1 className="font-serif text-4xl font-semibold text-text-primary mb-4">Browse Works</h1>
        <p className="text-text-secondary max-w-2xl mx-auto">
          Explore poetry, fiction, screenplays, essays, and more from our community of writers
        </p>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <Input
              type="text"
              placeholder="Search works by title, author, or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
              data-testid="stories-search"
            />
          </div>
          <Button type="submit" className="btn-primary h-12">Search</Button>
        </form>
        
        <Select value={selectedGenre || 'all'} onValueChange={handleGenreChange}>
          <SelectTrigger className="w-48 h-12" data-testid="genre-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Genres" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Genres</SelectItem>
            {genres.map((genre) => (
              <SelectItem key={genre} value={genre}>{genre}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stories Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-80 rounded-xl" />
          ))}
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-20">
          <BookOpen className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <h2 className="font-serif text-2xl font-semibold text-text-primary mb-2">No works found</h2>
          <p className="text-text-secondary">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stories.map((story) => (
            <Link 
              key={story.id} 
              to={`/story/${story.id}`}
              className="group"
              data-testid={`story-card-${story.id}`}
            >
              <Card className="h-full bg-white border-brand-primary/10 overflow-hidden card-hover">
                {story.cover_image && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img 
                      src={story.cover_image} 
                      alt={story.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      style={{ objectPosition: `center ${story.cover_image_position || 50}%` }}
                    />
                  </div>
                )}
                <CardContent className={`p-6 ${!story.cover_image ? 'pt-8' : ''}`}>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary">
                      {story.genre}
                    </span>
                    {(story.content?.match(/\[chapter:/g) || []).length > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium inline-flex items-center gap-0.5">
                        <BookOpen className="w-3 h-3" />
                        {(story.content.match(/\[chapter:/g) || []).length} chapters
                      </span>
                    )}
                    {story.tags.slice(0, 2).map(tag => (
                      <span key={tag} className="text-xs text-text-muted">#{tag}</span>
                    ))}
                    {story.awards?.length > 0 && story.awards.slice(0, 1).map((award) => (
                      <span 
                        key={`award-${award}`}
                        className="text-xs px-2 py-0.5 rounded-full bg-brand-accent/15 text-brand-accent font-medium inline-flex items-center gap-0.5"
                        data-testid={`story-card-award-${story.id}`}
                      >
                        <Trophy className="w-3 h-3" />
                        {award}
                      </span>
                    ))}
                  </div>
                  
                  <h3 className="font-serif text-xl font-semibold text-text-primary mb-2 group-hover:text-brand-accent transition-colors">
                    {story.title}
                  </h3>
                  
                  {story.summary && (
                    <p className="text-text-secondary text-sm line-clamp-3 mb-4">
                      {story.summary}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between pt-4 border-t border-brand-primary/10">
                    <div className="flex items-center gap-2 text-sm text-text-muted">
                      <User className="w-4 h-4" />
                      <span>{story.author_name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-text-muted">
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" /> {story.view_count}
                      </span>
                      <button
                        onClick={(e) => handleLike(e, story.id)}
                        className="flex items-center gap-1 hover:text-brand-accent transition-colors"
                        data-testid={`like-card-${story.id}`}
                      >
                        <Heart className="w-4 h-4" /> {story.like_count}
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default StoriesPage;
